import { Check, ChevronDown } from 'lucide-react';
import MenuButton from '../table/MenuButton';
import Badge from '../ui/Badge';
import { TONE_BADGE, TONE_DOT, type Tone } from '../ui/tone';

export interface StatusPillOption<K extends string> {
  value: K;
  label: string;
  tone: Tone;
  disabled?: boolean;
  /** Draw a hairline above this option (e.g. before "לא רלוונטי"). */
  separatorBefore?: boolean;
}

/**
 * P-51 (D-225 QX-443) - ONE status control for every screen: a pill in the status colour that opens a
 * list (popover on desktop, sheet on touch), the same look as the status pill of the tasks list
 * (`ticketTable.StatusCell`). Generic: the caller gives the options, their labels and tones.
 * Read-only (`disabled`, or no options to move to) = the plain status badge.
 */
export default function StatusPillSelect<K extends string>({
  value,
  options,
  onChange,
  label,
  sheetTitle,
  disabled = false,
  pending = false,
  className = '',
  prefix,
}: {
  value: K;
  options: readonly StatusPillOption<K>[];
  onChange: (next: K) => void;
  /** Accessible name of the trigger, e.g. "שינוי סטטוס: חדש". */
  label: string;
  sheetTitle?: string;
  disabled?: boolean;
  pending?: boolean;
  className?: string;
  /** D-317 §5 - an optional visible word before the status ("סטטוס: בטיפול"). None = as before. */
  prefix?: string;
}) {
  const current = options.find((o) => o.value === value);
  const tone: Tone = current?.tone ?? 'neutral';
  const text = current?.label ?? value;
  const movable = options.some((o) => o.value !== value && !o.disabled);
  if (disabled || !movable) return <Badge tone={tone} dot>{text}</Badge>;
  return (
    <MenuButton
      label={label}
      sheetTitle={sheetTitle ?? label}
      align="start"
      popoverClassName="w-60 p-1.5"
      triggerClassName={`inline-flex items-center gap-1.5 rounded-full py-0.5 pe-1.5 ps-2 text-xs font-medium whitespace-nowrap transition-shadow duration-micro hover:ring-1 hover:ring-control aria-expanded:ring-1 aria-expanded:ring-control disabled:opacity-60 ${TONE_BADGE[tone]} ${className}`}
      triggerProps={{ 'data-status-pill': String(value) }}
      items={options.map((o) => ({
        key: o.value,
        label: o.label,
        textValue: o.label,
        icon: <span className={`size-2 rounded-full ${TONE_DOT[o.tone]}`} />,
        trailing: o.value === value ? <Check size={16} strokeWidth={1.75} className="text-accent-text" aria-hidden="true" /> : undefined,
        disabled: pending || o.disabled,
        separatorBefore: o.separatorBefore,
        onSelect: () => { if (o.value !== value) onChange(o.value); },
      }))}
    >
      <span className={`size-1.5 shrink-0 rounded-full ${TONE_DOT[tone]}`} aria-hidden="true" />
      {prefix && <span className="font-normal">{prefix}:</span>}
      {text}
      <ChevronDown size={12} aria-hidden="true" />
    </MenuButton>
  );
}
